/* Service-worker registration + capture of the `beforeinstallprompt` event.
 *
 * Exported `usePWAInstall()` hook lets any React component show a custom
 * "Install app" button. The raw prompt event is saved to window so components
 * can subscribe via `window.addEventListener("pwa:can-install", ...)`.
 */
import { useEffect, useState } from "react";

let deferredPrompt = null;

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Only register on HTTPS (or localhost), never inside the editor iframe
  if (window.self !== window.top) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      // updateViaCache: 'none' is critical — without it Chrome applies its
      // normal HTTP cache to `/sw.js` itself (default TTL up to 24h). That
      // means after a deploy users can keep the OLD service worker active
      // for up to a day, serving stale bundles. This was the root cause of
      // the recurring "Error de red" on Google login (Android Chrome, prod
      // only, intermittent — Feb 2026 iter.209). Forcing 'none' makes the
      // browser always fetch sw.js fresh from network on every check,
      // pairing perfectly with the per-build CACHE_VERSION bump.
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then((reg) => {
        // Trigger an immediate update check so users on a previously-
        // installed SW pick up the new one within a few seconds instead
        // of waiting for the next 1h interval below.
        reg.update().catch(() => {});

        // Also re-check whenever the tab regains focus — covers the
        // "user kept the app open in the background for hours" case.
        const onFocus = () => reg.update().catch(() => {});
        window.addEventListener("focus", onFocus);

        // Background interval as a safety net (every 60 min).
        setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);

        // When a new SW is found and finishes installing while the page
        // is open, take control on the next reload via SKIP_WAITING so
        // the user doesn't need a manual hard refresh.
        reg.addEventListener("updatefound", () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              // A new SW is waiting. Ask it to skip waiting; on the next
              // navigation it will become the active SW.
              installing.postMessage("SKIP_WAITING");
            }
          });
        });
      })
      .catch(() => {
        // Silent — SW is an enhancement, not a requirement
      });
  });

  // Capture the beforeinstallprompt event so we can trigger it from a button
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new CustomEvent("pwa:can-install"));
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    window.dispatchEvent(new CustomEvent("pwa:installed"));
  });
}

export function usePWAInstall() {
  const [canInstall, setCanInstall] = useState(!!deferredPrompt);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onCan = () => setCanInstall(true);
    const onDone = () => { setCanInstall(false); setInstalled(true); };
    window.addEventListener("pwa:can-install", onCan);
    window.addEventListener("pwa:installed", onDone);
    return () => {
      window.removeEventListener("pwa:can-install", onCan);
      window.removeEventListener("pwa:installed", onDone);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return { outcome: "unavailable" };
    const p = deferredPrompt;
    deferredPrompt = null;
    p.prompt();
    const choice = await p.userChoice;
    if (choice.outcome === "accepted") {
      setCanInstall(false);
      setInstalled(true);
    }
    return choice;
  };

  return { canInstall, installed, install };
}

// iOS doesn't fire beforeinstallprompt — detect Safari and guide the user to
// the "Añadir a la pantalla de inicio" option manually.
export function isIOSSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

// Whether the app is already running in standalone (installed) mode
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true   // iOS
  );
}
